import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Card, Container } from "react-bootstrap";

export default function EditSlider() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const sliderId = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [currentBackgroundImage, setCurrentBackgroundImage] = useState("");
  const [slideType, setSlideType] = useState("text");

  const [cards, setCards] = useState([]);

  useEffect(() => {
    fetchSlider();
  }, [sliderId]);

  const fetchSlider = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/slider/${sliderId}`,
      );

      const slider = res.data.data || res.data;

      setTitle(slider.title || "");
      setDescription(slider.description || "");
      setButtonText(slider.button_text || "");
      setButtonUrl(slider.button_link || "");
      setSlideType(slider.slide_type || "text");

      setCurrentBackgroundImage(slider.background_image || "");

      setCards(
        slider.cards?.map((card) => ({
          id: card.id,
          title: card.title || "",
          description: card.description || "",
          existingImage: card.image || "",
          image: null,
        })) || [],
      );
    } catch (err) {
      setError("Failed to load slider");
    } finally {
      setLoading(false);
    }
  };

  const handleCardChange = (index, field, value) => {
    const updated = [...cards];
    updated[index][field] = value;
    setCards(updated);
  };

  const addCard = () => {
    if (cards.length < 3) {
      setCards([
        ...cards,
        {
          title: "",
          description: "",
          image: null,
          existingImage: "",
        },
      ]);
    }
  };

  const removeCard = (index) => {
    setCards(cards.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();

    formData.append("title", title);
    formData.append("description", description);
    formData.append("button_text", buttonText);
    formData.append("button_link", buttonUrl);
    formData.append("slide_type", slideType);

    if (backgroundImage) {
      formData.append("background_image", backgroundImage);
    }

    if (slideType === "cards") {
      cards.forEach((card, index) => {
        formData.append(`cards[${index}][id]`, card.id || "");
        formData.append(`cards[${index}][title]`, card.title);
        formData.append(`cards[${index}][description]`, card.description);

        if (card.image) {
          formData.append(`cards[${index}][image]`, card.image);
        }
      });
    }

    try {
      formData.append("_method", "PUT");

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/slider/${sliderId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      alert("Slider updated successfully");
      navigate("/xl-admin/slides");
    } catch (err) {
      console.error(err);
      alert("Failed to update slider");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Container className="my-5">
      <Card>
        <Card.Body>
          <h2>Edit Slide</h2>

          {error && <div className="alert alert-danger">{error}</div>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Background Image</Form.Label>

              {currentBackgroundImage && (
                <div className="mb-2">
                  <img
                    src={currentBackgroundImage}
                    alt=""
                    style={{
                      maxWidth: "300px",
                      maxHeight: "150px",
                    }}
                  />
                </div>
              )}

              <Form.Control
                type="file"
                onChange={(e) => setBackgroundImage(e.target.files[0])}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slide Title</Form.Label>
              <Form.Control
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slide Type</Form.Label>
              <Form.Select
                value={slideType}
                onChange={(e) => setSlideType(e.target.value)}
              >
                <option value="text">Text & Button</option>
                <option value="cards">Cards</option>
              </Form.Select>
            </Form.Group>

            {slideType === "text" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Button Text</Form.Label>
                  <Form.Control
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Button URL</Form.Label>
                  <Form.Control
                    value={buttonUrl}
                    onChange={(e) => setButtonUrl(e.target.value)}
                  />
                </Form.Group>
              </>
            )}

            {slideType === "cards" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Group>

                <h5>Cards</h5>

                {cards.map((card, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Body>
                      <Form.Group className="mb-2">
                        <Form.Label>Card Title</Form.Label>
                        <Form.Control
                          value={card.title}
                          onChange={(e) =>
                            handleCardChange(index, "title", e.target.value)
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-2">
                        <Form.Label>Card Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          value={card.description}
                          onChange={(e) =>
                            handleCardChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>

                      {card.existingImage && (
                        <div className="mb-2">
                          <img
                            src={card.existingImage}
                            alt=""
                            style={{
                              maxWidth: "150px",
                            }}
                          />
                        </div>
                      )}

                      <Form.Group className="mb-2">
                        <Form.Label>Card Image</Form.Label>
                        <Form.Control
                          type="file"
                          onChange={(e) =>
                            handleCardChange(index, "image", e.target.files[0])
                          }
                        />
                      </Form.Group>

                      <Button
                        variant="danger"
                        onClick={() => removeCard(index)}
                      >
                        Remove
                      </Button>
                    </Card.Body>
                  </Card>
                ))}

                {cards.length < 3 && (
                  <Button variant="secondary" onClick={addCard}>
                    Add Card
                  </Button>
                )}
              </>
            )}

            <Button type="submit" className="mt-3">
              Update Slider
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
